import {Component, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder} from '@angular/forms';
import {Store, select} from '@ngrx/store';
import {AdminPanelState} from '../../store/reducers/admin-panel.reducer';
import {Subject} from 'rxjs';
import {
  getSelectedUser, getSelectedUserNote,
  getUserDetailSelectedTab
} from '../../store/selectors/admin-panel.selector';
import {
  AddSolutionAction, CloneUserAction, CreateSupportTicketForUserAction, GetUserDetailAction, GetUserNoteAction,
  SendConfirmationEmailAction, SendPasswordAction,
  UpdateSolutionAction,
  UpdateUserAction, UpdateUserNoteAction
} from '../../store/actions/admin-panel.action';
import {MatDialog} from '@angular/material/dialog';
import {AdminPanelConfig} from '../../admin-panel.config';
import {AddSolutionComponent} from '../add-solution/add-solution.component';
import {
  AddSolutionCommand, CloneUserCommand, CreateSupportTicketForUserCommand, SendConfirmationEmailCommand,
  SendPasswordCommand,
  UpdateSolutionCommand,
  UpdateUserCommand, UpdateUserNoteCommand
} from '../../commands/admin-panel.command';
import {takeUntil} from 'rxjs/operators';
import {UpdateSolutionComponent} from '../update-solution/update-solution.component';
import {UserModel} from '../../models/user.model';
import {SelectableSettings} from '@progress/kendo-angular-grid';
import {CloneUserComponent} from '../clone-user/clone-user.component';
import {CreateSupportTicketComponent} from '../create-support-ticket/create-support-ticket.component';
import {SendPasswordComponent} from '../send-password/send-password.component';
import {ParticleService} from '@core/services/particle.service';
import {SmartGridService} from '@core/services/smart-grid.service';

@Component({
    standalone: false,
    selector: 'lib-detail-user',
  templateUrl: './detail-user.component.html',
  styleUrls: ['./detail-user.component.scss']
})
export class DetailUserComponent implements OnInit, OnDestroy {

  selectableSettings: SelectableSettings = {
    enabled: false,
    checkboxOnly: true
  };
  item: any;
  user: any;
  userNote: any;
  isEditing: any;
  sourceType = 'Solution';
  unsubscribe$ = new Subject<void>();
  detailForm: any;
  companyId: string;
  userName: string;
  pageable: boolean;
  selectedTabIndex: number;

  constructor(public dialog: MatDialog,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private formBuilder: FormBuilder,
              private store: Store<AdminPanelState>,
              private particleService: ParticleService,
              private smartGridService: SmartGridService,
              private location: Location) {
    this.user = null;
    this.userName = '';
    this.userNote = null;
    this.pageable = false;
    this.detailForm = null;
    this.selectedTabIndex = 0;
  }

  ngOnInit() {
    this.initForm();

    this.activatedRoute.params.subscribe((p: any) => {
      this.userName = p['userName'];
      this.store.dispatch(new GetUserDetailAction(this.userName));

      this.store.dispatch(new GetUserNoteAction(this.userName));

      AdminPanelConfig.setUserName(this.sourceType, this.userName);
      this.smartGridService.initDataProvider(this.sourceType);
    });

    this.store.pipe(select(getSelectedUser), takeUntil(this.unsubscribe$)).subscribe(u => {
      this.user = u;

      if (this.user) {
        this.detailForm.controls['firstName'].enable();
        this.detailForm.controls['lastName'].enable();
        this.detailForm.controls['isActive'].enable();

        this.detailForm.controls['firstName'].setValue(this.user.firstName);
        this.detailForm.controls['lastName'].setValue(this.user.lastName);
        this.detailForm.controls['companyName'].setValue(this.user.companyName);
        this.detailForm.controls['designation'].setValue(this.user.designation);
        this.detailForm.controls['email'].setValue(this.user.email);
        this.detailForm.controls['isActive'].setValue(this.user.isActive);
      }
    });

    this.store.pipe(select(getSelectedUserNote), takeUntil(this.unsubscribe$)).subscribe(u => {
      this.userNote = u;

      if (this.userNote) {
        this.detailForm.controls['userNote'].enable();

        this.detailForm.controls['userNote'].setValue(this.userNote.notes);
      }
    });

    /*this.store.pipe(select(getUserDetailSelectedTab), takeUntil(this.unsubscribe$)).subscribe(u => {
      this.selectedTabIndex = u;
    });*/
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  back() {
    /*this.router.navigate(AdminPanelConfig.UserGridUrl(), {relativeTo: this.activatedRoute.parent});*/
    this.location.back();
  }

  initForm() {
    this.detailForm = this.formBuilder.group({
      firstName: [''],
      lastName: [''],
      companyName: [''],
      designation: [''],
      email: [''],
      userNote: [''],
      isActive: ['']
    });
    this.detailForm.disable();

    this.isEditing = {
      firstName: false,
      lastName: false,
      userNote: false
    };
  }

  addSolution() {
    const dialogRef = this.dialog.open(AddSolutionComponent, {
      width: '600px',
      disableClose: true,
      data: {userName: this.user.userName}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const solutions = result.solutions;
        const command = new AddSolutionCommand(solutions);
        this.store.dispatch(new AddSolutionAction(command));
      }
    });
  }

  cloneUser() {
    const dialogRef = this.dialog.open(CloneUserComponent, {
      width: '600px',
      disableClose: true,
      data: {user: this.user, companyId: this.user.companyId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const user = result.user;
        const clonedUserEmail = result.email;
        const clonedUserCompanyId = result.companyId;
        const command = new CloneUserCommand(user, clonedUserEmail, clonedUserCompanyId);
        this.store.dispatch(new CloneUserAction(command));
      }
    });
  }

  onSelectionChanged(event) {

  }

  onValueChanged(event) {
    if (event.action && event.action.id && event.action.id === 'Edit') {
      this.updateSolution(event.item);
    }
  }

  updateSolution(item) {
    const dialogRef = this.dialog.open(UpdateSolutionComponent, {
      width: '600px',
      disableClose: true,
      data: {solution: this.getSolution(item.cells)}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const solution = result.solution;
        const command = new UpdateSolutionCommand(solution);
        this.store.dispatch(new UpdateSolutionAction(command));
      }
    });
  }

  getSolution(solutionData) {
    const solution = {};

    for (const field in solutionData) {
      if (solutionData.hasOwnProperty(field)) {
        solution[field] = solutionData[field].displayText;
      }
    }

    return solution;
  }

  editFirstName() {
    this.resetEditing();
    this.isEditing.firstName = true;
  }

  editLastName() {
    this.resetEditing();
    this.isEditing.lastName = true;
  }

  editUserNote() {
    this.resetEditing();
    this.isEditing.userNote = true;
  }

  updateFirstName(event) {
    this.detailForm.controls['firstName'].setValue(event.text);
    this.resetEditing();
    this.updateUserData();
  }

  updateLastName(event) {
    this.detailForm.controls['lastName'].setValue(event.text);
    this.resetEditing();
    this.updateUserData();
  }

  updateUserNote(event) {
    this.detailForm.controls['userNote'].setValue(event.text);
    this.resetEditing();
    this.updateUserNoteData();
  }

  resetEditing() {
    for (const field in this.isEditing) {
      if (this.isEditing.hasOwnProperty(field)) {
        this.isEditing[field] = false;
      }
    }
  }

  updateUserNoteData() {
    this.detailForm.controls['userNote'].setValue(this.detailForm.controls['userNote'].value.trim());

    const userNoteObj = {
      userName: this.userName,
      notes: this.detailForm.controls['userNote'].value
    };

    const command = new UpdateUserNoteCommand(userNoteObj);
    this.store.dispatch(new UpdateUserNoteAction(command));
  }

  updateUserData() {
    this.detailForm.controls['firstName'].setValue(this.detailForm.controls['firstName'].value.trim());
    this.detailForm.controls['lastName'].setValue(this.detailForm.controls['lastName'].value.trim());
    this.detailForm.controls['isActive'].setValue(this.detailForm.controls['isActive'].value);

    const user = new UserModel({
      firstName: this.detailForm.controls['firstName'].value.trim(),
      lastName: this.detailForm.controls['lastName'].value.trim(),
      companyName: this.user.companyName,
      designation: this.user.designation,
      isActive: this.detailForm.controls['isActive'].value,
      username: this.user.userName,
      email: this.user.email,
      companyId: this.user.companyId
    });

    const command = new UpdateUserCommand(user);
    this.store.dispatch(new UpdateUserAction(command));
  }

  sendPassword() {
    const dialogRef = this.dialog.open(SendPasswordComponent, {
      width: '600px',
      disableClose: true,
      data: {user: this.user}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const command = new SendPasswordCommand(this.user.userName, result.sendPassword.generatePassword, result.sendPassword.password,
          result.sendPassword.passwordChange, result.sendPassword.sendCopyToMe);
        this.store.dispatch(new SendPasswordAction(command));
      }
    });
  }

  createSupportTicket() {
    const dialogRef = this.dialog.open(CreateSupportTicketComponent, {
      width: '600px',
      disableClose: true,
      data: {userCompanyId: this.companyId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const supportTicket = {
          title: result.supportTicket.title,
          description: result.supportTicket.description,
          fileAttachments: [],
          userName: this.user.email,
          userCompanyId: this.user.companyId,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          email: this.user.email,
          packageCode: ''
        };

        const command = new CreateSupportTicketForUserCommand(supportTicket.title, supportTicket.description,
          supportTicket.fileAttachments, supportTicket.userName, supportTicket.userCompanyId, supportTicket.firstName,
          supportTicket.lastName, supportTicket.email, supportTicket.packageCode);
        this.store.dispatch(new CreateSupportTicketForUserAction(command));
      }
    });
  }
}
